import { Partner } from './database';

// Achievement data types
export interface Achievement {
  id: string;
  couple_id: string;
  name: string;
  description: string;
  icon: string;
  points_awarded: number;
  unlocked_at: string;
  unlocked_by: string;
}

export interface AchievementTemplate {
  id: string;
  name: string;
  description: string;
  icon: string;
  points_reward: number;
  category: AchievementCategory;
  rarity: AchievementRarity;
  criteria: AchievementCriteria;
  unlock_conditions: AchievementConditions;
  is_hidden: boolean;
  is_secret: boolean;
}

export interface AchievementProgress {
  template_id: string;
  current_value: number;
  target_value: number;
  progress_percentage: number;
  is_unlocked: boolean;
  is_close_to_unlock: boolean;
}

export interface UnlockedAchievement extends Achievement {
  template: AchievementTemplate;
  time_ago: string;
  is_new: boolean;
}

// Request/Response types
export interface CheckAchievementsRequest {
  couple_id: string;
  trigger_type: AchievementTrigger;
  trigger_data?: AchievementTriggerData;
}

export interface CheckAchievementsResponse {
  success: boolean;
  new_achievements?: Achievement[];
  points_awarded?: number;
  error?: string;
}

export interface GetAchievementsRequest {
  couple_id: string;
  include_progress?: boolean;
}

export interface GetAchievementsResponse {
  success: boolean;
  unlocked_achievements?: UnlockedAchievement[];
  locked_achievements?: AchievementTemplate[];
  achievement_progress?: AchievementProgress[];
  stats?: AchievementStats;
  error?: string;
}

export interface AwardAchievementRequest {
  couple_id: string;
  template_id: string;
  unlocked_by: Partner;
  points_awarded: number;
}

export interface AwardAchievementResponse {
  success: boolean;
  achievement?: Achievement;
  error?: string;
}

// Achievement statistics
export interface AchievementStats {
  total_unlocked: number;
  total_available: number;
  completion_percentage: number;
  points_from_achievements: number;
  recent_unlocks: number;
  rarity_breakdown: {
    [key in AchievementRarity]: number;
  };
  category_breakdown: {
    [key in AchievementCategory]: number;
  };
}

// Achievement criteria and conditions
export interface AchievementCriteria {
  type: AchievementType;
  target_value: number;
  timeframe?: AchievementTimeframe;
  category_id?: string;
  partner_specific?: boolean;
}

export interface AchievementConditions {
  min_expenses?: number;
  min_days_active?: number;
  requires_challenge_completion?: boolean;
  requires_streak?: number;
  excludes?: string[];
}

export interface AchievementTriggerData {
  expense_amount?: number;
  expense_category_id?: string;
  challenge_id?: string;
  challenge_type?: string;
  streak_days?: number;
  spending_total?: number;
  category_diversity?: number;
  expense_id?: string;
}

// Enums
export enum AchievementCategory {
  SPENDING = 'spending',
  CHALLENGES = 'challenges',
  STREAKS = 'streaks',
  CATEGORIES = 'categories',
  TEAMWORK = 'teamwork',
  MILESTONES = 'milestones',
  SPECIAL = 'special'
}

export enum AchievementRarity {
  COMMON = 'common',
  UNCOMMON = 'uncommon',
  RARE = 'rare',
  EPIC = 'epic',
  LEGENDARY = 'legendary'
}

export enum AchievementType {
  EXPENSE_COUNT = 'expense_count',
  EXPENSE_AMOUNT = 'expense_amount',
  CATEGORY_USAGE = 'category_usage',
  CHALLENGE_COMPLETION = 'challenge_completion',
  STREAK_ACHIEVEMENT = 'streak_achievement',
  SPENDING_LIMIT = 'spending_limit',
  SAVINGS_GOAL = 'savings_goal',
  CATEGORY_DIVERSITY = 'category_diversity',
  TEAMWORK_BALANCE = 'teamwork_balance',
  TIME_MILESTONE = 'time_milestone',
  SPECIAL_EVENT = 'special_event'
}

export enum AchievementTrigger {
  EXPENSE_ADDED = 'expense_added',
  CHALLENGE_COMPLETED = 'challenge_completed',
  STREAK_UPDATED = 'streak_updated',
  DAILY_CHECK = 'daily_check',
  MANUAL_CHECK = 'manual_check'
}

export enum AchievementTimeframe {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
  ALL_TIME = 'all_time'
}

// Achievement templates - predefined achievements
export const ACHIEVEMENT_TEMPLATES: AchievementTemplate[] = [
  // Spending achievements
  {
    id: 'first_expense',
    name: 'First Steps',
    description: 'Add your first expense to start tracking your spending',
    icon: 'target',
    points_reward: 25,
    category: AchievementCategory.SPENDING,
    rarity: AchievementRarity.COMMON,
    criteria: {
      type: AchievementType.EXPENSE_COUNT,
      target_value: 1
    },
    unlock_conditions: {},
    is_hidden: false,
    is_secret: false
  },
  {
    id: 'expense_tracker',
    name: 'Expense Tracker',
    description: 'Add 10 expenses to establish your tracking habit',
    icon: 'list-checks',
    points_reward: 100,
    category: AchievementCategory.SPENDING,
    rarity: AchievementRarity.COMMON,
    criteria: {
      type: AchievementType.EXPENSE_COUNT,
      target_value: 10
    },
    unlock_conditions: {},
    is_hidden: false,
    is_secret: false
  },
  {
    id: 'big_spender',
    name: 'Big Spender',
    description: 'Log an expense over $100',
    icon: 'banknote',
    points_reward: 50,
    category: AchievementCategory.SPENDING,
    rarity: AchievementRarity.UNCOMMON,
    criteria: {
      type: AchievementType.EXPENSE_AMOUNT,
      target_value: 100
    },
    unlock_conditions: {},
    is_hidden: false,
    is_secret: false
  },
  
  // Challenge achievements
  {
    id: 'challenge_starter',
    name: 'Challenge Accepted',
    description: 'Complete your first challenge',
    icon: 'trophy',
    points_reward: 75,
    category: AchievementCategory.CHALLENGES,
    rarity: AchievementRarity.COMMON,
    criteria: {
      type: AchievementType.CHALLENGE_COMPLETION,
      target_value: 1
    },
    unlock_conditions: {},
    is_hidden: false,
    is_secret: false
  },
  {
    id: 'challenge_champion',
    name: 'Challenge Champion',
    description: 'Complete 5 challenges',
    icon: 'crown',
    points_reward: 200,
    category: AchievementCategory.CHALLENGES,
    rarity: AchievementRarity.RARE,
    criteria: {
      type: AchievementType.CHALLENGE_COMPLETION,
      target_value: 5
    },
    unlock_conditions: {},
    is_hidden: false,
    is_secret: false
  },
  
  // Category achievements
  {
    id: 'category_explorer',
    name: 'Category Explorer',
    description: 'Use 5 different spending categories',
    icon: 'compass',
    points_reward: 75,
    category: AchievementCategory.CATEGORIES,
    rarity: AchievementRarity.COMMON,
    criteria: {
      type: AchievementType.CATEGORY_DIVERSITY,
      target_value: 5
    },
    unlock_conditions: {},
    is_hidden: false,
    is_secret: false
  },
  {
    id: 'category_master',
    name: 'Category Master',
    description: 'Use all 10 default categories',
    icon: 'grid-3x3',
    points_reward: 150,
    category: AchievementCategory.CATEGORIES,
    rarity: AchievementRarity.UNCOMMON,
    criteria: {
      type: AchievementType.CATEGORY_DIVERSITY,
      target_value: 10
    },
    unlock_conditions: {},
    is_hidden: false,
    is_secret: false
  },
  
  // Streak achievements
  {
    id: 'consistency_rookie',
    name: 'Consistency Rookie',
    description: 'Maintain a 3-day expense tracking streak',
    icon: 'flame',
    points_reward: 50,
    category: AchievementCategory.STREAKS,
    rarity: AchievementRarity.COMMON,
    criteria: {
      type: AchievementType.STREAK_ACHIEVEMENT,
      target_value: 3
    },
    unlock_conditions: {},
    is_hidden: false,
    is_secret: false
  },
  {
    id: 'streak_master',
    name: 'Streak Master',
    description: 'Maintain a 7-day expense tracking streak',
    icon: 'zap',
    points_reward: 125,
    category: AchievementCategory.STREAKS,
    rarity: AchievementRarity.UNCOMMON,
    criteria: {
      type: AchievementType.STREAK_ACHIEVEMENT,
      target_value: 7
    },
    unlock_conditions: {},
    is_hidden: false,
    is_secret: false
  },
  
  // Teamwork achievements
  {
    id: 'perfect_balance',
    name: 'Perfect Balance',
    description: 'Both partners contribute equally to expenses in a week',
    icon: 'scale',
    points_reward: 100,
    category: AchievementCategory.TEAMWORK,
    rarity: AchievementRarity.UNCOMMON,
    criteria: {
      type: AchievementType.TEAMWORK_BALANCE,
      target_value: 1,
      timeframe: AchievementTimeframe.WEEKLY
    },
    unlock_conditions: {
      min_expenses: 4
    },
    is_hidden: false,
    is_secret: false
  },
  
  // Milestone achievements
  {
    id: 'first_week',
    name: 'First Week Together',
    description: 'Complete your first week of expense tracking',
    icon: 'calendar-days',
    points_reward: 100,
    category: AchievementCategory.MILESTONES,
    rarity: AchievementRarity.COMMON,
    criteria: {
      type: AchievementType.TIME_MILESTONE,
      target_value: 7
    },
    unlock_conditions: {
      min_expenses: 3
    },
    is_hidden: false,
    is_secret: false
  },
  {
    id: 'first_month',
    name: 'Monthly Milestone',
    description: 'Complete your first month of expense tracking',
    icon: 'calendar',
    points_reward: 250,
    category: AchievementCategory.MILESTONES,
    rarity: AchievementRarity.RARE,
    criteria: {
      type: AchievementType.TIME_MILESTONE,
      target_value: 30
    },
    unlock_conditions: {
      min_expenses: 10
    },
    is_hidden: false,
    is_secret: false
  },
  
  // Special/Hidden achievements
  {
    id: 'penny_pincher',
    name: 'Penny Pincher',
    description: 'Log an expense under $1',
    icon: 'coins',
    points_reward: 25,
    category: AchievementCategory.SPECIAL,
    rarity: AchievementRarity.UNCOMMON,
    criteria: {
      type: AchievementType.EXPENSE_AMOUNT,
      target_value: 1
    },
    unlock_conditions: {},
    is_hidden: true,
    is_secret: false
  },
  {
    id: 'midnight_spender',
    name: 'Midnight Spender',
    description: 'Add an expense between 11 PM and 1 AM',
    icon: 'moon',
    points_reward: 50,
    category: AchievementCategory.SPECIAL,
    rarity: AchievementRarity.RARE,
    criteria: {
      type: AchievementType.SPECIAL_EVENT,
      target_value: 1
    },
    unlock_conditions: {},
    is_hidden: true,
    is_secret: true
  }
];

// Achievement validation constants
export const ACHIEVEMENT_VALIDATION = {
  MIN_NAME_LENGTH: 3,
  MAX_NAME_LENGTH: 50,
  MIN_DESCRIPTION_LENGTH: 10,
  MAX_DESCRIPTION_LENGTH: 200,
  MIN_POINTS: 1,
  MAX_POINTS: 1000,
  CLOSE_TO_UNLOCK_THRESHOLD: 0.8, // 80% progress
} as const;

// Achievement display constants
export const ACHIEVEMENT_DISPLAY = {
  RARITY_COLORS: {
    [AchievementRarity.COMMON]: '#10b981', // emerald-500
    [AchievementRarity.UNCOMMON]: '#3b82f6', // blue-500
    [AchievementRarity.RARE]: '#8b5cf6', // violet-500
    [AchievementRarity.EPIC]: '#f59e0b', // amber-500
    [AchievementRarity.LEGENDARY]: '#ef4444', // red-500
  },
  CATEGORY_ICONS: {
    [AchievementCategory.SPENDING]: 'dollar-sign',
    [AchievementCategory.CHALLENGES]: 'trophy',
    [AchievementCategory.STREAKS]: 'flame',
    [AchievementCategory.CATEGORIES]: 'grid-3x3',
    [AchievementCategory.TEAMWORK]: 'users',
    [AchievementCategory.MILESTONES]: 'calendar',
    [AchievementCategory.SPECIAL]: 'star',
  },
  LOCKED_ACHIEVEMENT_ICON: 'lock',
  SECRET_ACHIEVEMENT_NAME: 'Secret Achievement',
  SECRET_ACHIEVEMENT_DESCRIPTION: 'Complete special actions to unlock this achievement',
} as const;